import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import { API, useAuth } from "../App";
import { useWebSocket } from "../hooks/useWebSocket";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  MessageCircle, Send, Loader2, Search, User, Package,
  Clock, CheckCircle, ArrowLeft, RefreshCw, Bell, Phone,
  MapPin, Calendar, Truck, AlertCircle, Plus, MoreVertical,
  Trash2, Copy, UserPlus
} from "lucide-react";

export default function ChatMensajeriaPage() {
  const { user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [directChats, setDirectChats] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [unreadCounts, setUnreadCounts] = useState({});
  
  // New chat dialog
  const [showNewChatDialog, setShowNewChatDialog] = useState(false);
  const [mensajeros, setMensajeros] = useState([]);
  const [selectedMensajero, setSelectedMensajero] = useState("");
  const [initialMessage, setInitialMessage] = useState("");
  const [creatingChat, setCreatingChat] = useState(false);
  
  const messagesEndRef = useRef(null);
  const { isConnected, onMessage, joinRoom, leaveRoom } = useWebSocket(user?.id);

  // Fetch conversations (solicitudes with chat)
  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      const [solicitudesRes, directosRes] = await Promise.all([
        axios.get(`${API}/mensajeria/solicitudes`, getAuthHeaders()),
        axios.get(`${API}/chat/directos`, getAuthHeaders()).catch(() => ({ data: [] }))
      ]);
      
      // Filter to show solicitudes that have been assigned to a mensajero
      const withMensajero = solicitudesRes.data.filter(s => s.mensajero_id);
      setConversations(withMensajero);
      setDirectChats(directosRes.data || []);
      
    } catch (err) {
      console.error("Error fetching conversations:", err);
      toast.error("Error al cargar conversaciones");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Fetch mensajeros for new chat
  const fetchMensajeros = async () => {
    try {
      const res = await axios.get(`${API}/chat/mensajeros`, getAuthHeaders());
      setMensajeros(res.data || []);
    } catch (err) {
      console.error("Error fetching mensajeros:", err);
    }
  };

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // WebSocket handlers
  useEffect(() => {
    const unsubNewMessage = onMessage("new_message", (data) => {
      if (selectedConversation && data.message.solicitud_id === selectedConversation.id) {
        setMessages(prev => [...prev, data.message]);
      } else {
        // Update unread count for that conversation
        setUnreadCounts(prev => ({
          ...prev,
          [data.message.solicitud_id]: (prev[data.message.solicitud_id] || 0) + 1
        }));
        toast.info(`Nuevo mensaje de ${data.message.sender_name}`);
      }
      // Refresh conversations to update last message
      fetchConversations();
    });

    // Handle message deleted
    const unsubDeleted = onMessage("message_deleted", (data) => {
      if (selectedConversation && data.solicitud_id === selectedConversation.id) {
        setMessages(prev => prev.filter(m => m.id !== data.message_id));
      }
    });

    return () => {
      unsubNewMessage();
      unsubDeleted();
    };
  }, [onMessage, selectedConversation, fetchConversations]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Create new chat
  const handleCreateNewChat = async () => {
    if (!selectedMensajero) {
      toast.error("Selecciona un mensajero");
      return;
    }
    
    setCreatingChat(true);
    try {
      const res = await axios.post(`${API}/chat/new`, {
        mensajero_id: selectedMensajero,
        mensaje: initialMessage
      }, getAuthHeaders());
      
      toast.success("Chat creado exitosamente");
      setShowNewChatDialog(false);
      setSelectedMensajero("");
      setInitialMessage("");
      fetchConversations();
      
      // Open the new chat
      openConversation(res.data);
    } catch (err) {
      console.error("Error creating chat:", err);
      toast.error("Error al crear chat");
    } finally {
      setCreatingChat(false);
    }
  };

  // Delete message
  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm("¿Eliminar este mensaje?")) return;
    
    try {
      await axios.delete(`${API}/chat/messages/${messageId}`, getAuthHeaders());
      setMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success("Mensaje eliminado");
    } catch (err) {
      console.error("Error deleting message:", err);
      if (err.response?.status === 403) {
        toast.error("Solo puedes eliminar tus propios mensajes");
      } else {
        toast.error("Error al eliminar mensaje");
      }
    }
  };

  // Copy message
  const handleCopyMessage = (content) => {
    navigator.clipboard.writeText(content);
    toast.success("Mensaje copiado");
  };

  // Open conversation
  const openConversation = async (conversation) => {
    setSelectedConversation(conversation);
    setLoadingMessages(true);
    
    try {
      const chatId = conversation.id || conversation.chat_id;
      const res = await axios.get(`${API}/chat/messages/${chatId}`, getAuthHeaders());
      setMessages(res.data);
      
      // Clear unread count for this conversation
      setUnreadCounts(prev => ({ ...prev, [chatId]: 0 }));
      
      // Join WebSocket room
      joinRoom(`chat_${chatId}`);
    } catch (err) {
      console.error("Error loading messages:", err);
      toast.error("Error al cargar mensajes");
    } finally {
      setLoadingMessages(false);
    }
  };

  // Close conversation
  const closeConversation = () => {
    if (selectedConversation) {
      leaveRoom(`chat_${selectedConversation.id}`);
    }
    setSelectedConversation(null);
    setMessages([]);
  };

  // Send message
  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSendingMessage(true);
    try {
      const receiverId = selectedConversation.mensajero_id;
      
      await axios.post(`${API}/chat/messages`, {
        content: newMessage,
        solicitud_id: selectedConversation.id,
        receiver_id: receiverId
      }, getAuthHeaders());
      
      setNewMessage("");
    } catch (err) {
      console.error("Error sending message:", err);
      toast.error("Error al enviar mensaje");
    } finally {
      setSendingMessage(false);
    }
  };

  // Handle Enter key
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Filter conversations
  const filteredConversations = [...conversations, ...directChats].filter(c => 
    c.cliente?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.destino_nombre?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.mensajero_nombre?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get status badge
  const getStatusBadge = (estado) => {
    const statusConfig = {
      pendiente: { label: "Pendiente", className: "bg-amber-100 text-amber-700" },
      asignada: { label: "Asignada", className: "bg-blue-100 text-blue-700" },
      en_transito: { label: "En Tránsito", className: "bg-purple-100 text-purple-700" },
      entregada: { label: "Entregada", className: "bg-green-100 text-green-700" },
      cancelada: { label: "Cancelada", className: "bg-red-100 text-red-700" }
    };
    const config = statusConfig[estado] || { label: estado, className: "bg-slate-100 text-slate-700" };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  // Format time
  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-0 h-screen" data-testid="chat-mensajeria-page">
        <div className="h-full flex">
          {/* Conversations List */}
          <div className={`w-96 border-r dark:border-slate-700 bg-white dark:bg-slate-800 flex flex-col ${selectedConversation ? 'hidden md:flex' : 'flex'}`}>
            {/* Header */}
            <div className="p-4 border-b dark:border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <MessageCircle className="w-6 h-6 text-blue-500" />
                  Chat Mensajería
                </h1>
                <div className="flex items-center gap-1">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => { fetchMensajeros(); setShowNewChatDialog(true); }}
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    data-testid="new-chat-btn"
                  >
                    <Plus className="w-5 h-5" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={fetchConversations}>
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar conversaciones..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-conversations"
                />
              </div>
            </div>

            {/* New Chat Button (visible) */}
            <div className="p-3 border-b dark:border-slate-700">
              <Button 
                className="w-full bg-blue-500 hover:bg-blue-600 gap-2"
                onClick={() => { fetchMensajeros(); setShowNewChatDialog(true); }}
              >
                <UserPlus className="w-4 h-4" />
                Nuevo Chat
              </Button>
            </div>

            {/* Conversations */}
            <ScrollArea className="flex-1">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <MessageCircle className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">No hay conversaciones</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Inicia un chat nuevo con un mensajero
                  </p>
                </div>
              ) : (
                <div className="divide-y dark:divide-slate-700">
                  {filteredConversations.map(conv => (
                    <div
                      key={conv.id}
                      onClick={() => openConversation(conv)}
                      className={`p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors ${
                        selectedConversation?.id === conv.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                      }`}
                      data-testid={`conversation-${conv.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <Avatar className="w-10 h-10 flex-shrink-0">
                          <AvatarFallback className="bg-blue-100 text-blue-600">
                            <Truck className="w-5 h-5" />
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-sm truncate">{conv.destino_nombre || conv.cliente}</p>
                            <span className="text-xs text-slate-400">{formatTime(conv.updated_at || conv.created_at)}</span>
                          </div>
                          <p className="text-xs text-slate-500 truncate mt-0.5">
                            <span className="font-medium">Mensajero:</span> {conv.mensajero_nombre || "Sin asignar"}
                          </p>
                          <div className="flex items-center justify-between mt-1">
                            {getStatusBadge(conv.estado)}
                            {unreadCounts[conv.id] > 0 && (
                              <Badge className="bg-red-500 text-white text-xs">
                                {unreadCounts[conv.id]}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Chat Area */}
          <div className={`flex-1 flex flex-col bg-slate-100 dark:bg-slate-900 ${!selectedConversation ? 'hidden md:flex' : 'flex'}`}>
            {!selectedConversation ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageCircle className="w-16 h-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
                  <h2 className="text-xl font-medium text-slate-600 dark:text-slate-400">
                    Selecciona una conversación
                  </h2>
                  <p className="text-slate-400 dark:text-slate-500 mt-2">
                    Elige una solicitud para ver y enviar mensajes
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Chat Header */}
                <div className="p-4 bg-white dark:bg-slate-800 border-b dark:border-slate-700 flex items-center gap-4">
                  <Button variant="ghost" size="icon" onClick={closeConversation} className="md:hidden">
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                  <Avatar className="w-10 h-10">
                    <AvatarFallback className="bg-blue-100 text-blue-600">
                      <User className="w-5 h-5" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h2 className="font-semibold">{selectedConversation.mensajero_nombre || "Mensajero"}</h2>
                      {getStatusBadge(selectedConversation.estado)}
                    </div>
                    <p className="text-sm text-slate-500">
                      Entrega: {selectedConversation.destino_nombre || selectedConversation.cliente}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {isConnected && (
                      <Badge variant="outline" className="text-green-600 border-green-300">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                        En línea
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Delivery Info Card */}
                <div className="p-4 bg-white dark:bg-slate-800 border-b dark:border-slate-700">
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                      <MapPin className="w-4 h-4" />
                      <span className="truncate max-w-[200px]">{selectedConversation.direccion_destino || selectedConversation.direccion_envio}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                      <Package className="w-4 h-4" />
                      <span>{selectedConversation.tipo_solicitud || "Envío"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(selectedConversation.created_at).toLocaleDateString('es-ES')}</span>
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                  {loadingMessages ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <MessageCircle className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                        <p className="text-slate-500">No hay mensajes aún</p>
                        <p className="text-sm text-slate-400">Envía un mensaje para iniciar la conversación</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {messages.map((msg, idx) => {
                        const isOwn = msg.sender_id === user?.id;
                        return (
                          <div
                            key={msg.id || idx}
                            className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group`}
                          >
                            <div className={`relative max-w-[70%] rounded-2xl px-4 py-2 ${
                              isOwn 
                                ? 'bg-blue-500 text-white rounded-br-md' 
                                : 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-bl-md shadow-sm'
                            }`}>
                              {!isOwn && (
                                <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">
                                  {msg.sender_name}
                                </p>
                              )}
                              <p className="text-sm whitespace-pre-wrap pr-6">{msg.content}</p>
                              <p className={`text-xs mt-1 ${isOwn ? 'text-blue-100' : 'text-slate-400'}`}>
                                {formatTime(msg.created_at)}
                                {isOwn && msg.read && " ✓✓"}
                              </p>
                              
                              {/* Message actions menu */}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className={`absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity ${
                                      isOwn ? 'hover:bg-blue-600 text-white' : 'hover:bg-slate-100 text-slate-500'
                                    }`}
                                  >
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align={isOwn ? "end" : "start"}>
                                  <DropdownMenuItem onClick={() => handleCopyMessage(msg.content)}>
                                    <Copy className="w-4 h-4 mr-2" /> Copiar
                                  </DropdownMenuItem>
                                  {isOwn && (
                                    <DropdownMenuItem 
                                      onClick={() => handleDeleteMessage(msg.id)}
                                      className="text-red-600 focus:text-red-600"
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </ScrollArea>

                {/* Message Input */}
                <div className="p-4 bg-white dark:bg-slate-800 border-t dark:border-slate-700">
                  <div className="flex items-center gap-3">
                    <Input
                      value={newMessage}
                      onChange={e => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Escribe un mensaje..."
                      className="flex-1"
                      disabled={sendingMessage}
                      data-testid="message-input"
                    />
                    <Button 
                      onClick={sendMessage} 
                      disabled={!newMessage.trim() || sendingMessage}
                      className="bg-blue-500 hover:bg-blue-600"
                      data-testid="send-message-btn"
                    >
                      {sendingMessage ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* New Chat Dialog */}
        <Dialog open={showNewChatDialog} onOpenChange={setShowNewChatDialog}>
          <DialogContent className="dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-blue-500" />
                Nuevo Chat
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Seleccionar Mensajero</Label>
                <Select value={selectedMensajero} onValueChange={setSelectedMensajero}>
                  <SelectTrigger data-testid="select-mensajero">
                    <SelectValue placeholder="Elige un mensajero" />
                  </SelectTrigger>
                  <SelectContent>
                    {mensajeros.length === 0 ? (
                      <div className="p-2 text-sm text-slate-500 text-center">
                        No hay mensajeros disponibles
                      </div>
                    ) : (
                      mensajeros.map(m => (
                        <SelectItem key={m.id} value={m.id}>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            <span>{m.nombre}</span>
                            {m.estado === "disponible" && (
                              <Badge className="bg-green-100 text-green-700 text-xs">Disponible</Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Mensaje inicial (opcional)</Label>
                <Textarea
                  value={initialMessage}
                  onChange={e => setInitialMessage(e.target.value)}
                  placeholder="Escribe un mensaje para iniciar la conversación..."
                  rows={3}
                  data-testid="initial-message-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewChatDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleCreateNewChat} 
                disabled={!selectedMensajero || creatingChat}
                className="bg-blue-500 hover:bg-blue-600"
                data-testid="create-chat-btn"
              >
                {creatingChat ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <MessageCircle className="w-4 h-4 mr-2" />
                )}
                Iniciar Chat
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
